import Exit16 from '@carbon/icons-react/es/exit/16';
import Identification16 from '@carbon/icons-react/es/identification/16';
import OverflowMenuVertical20 from '@carbon/icons-react/es/overflow-menu--vertical/20';
import Reset16 from '@carbon/icons-react/es/reset/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Dropdown, Menu, Tooltip } from 'antd';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { PermissionSetType, ProjectId, User, UserType } from 'api/models';
import { MembersMeta } from 'api/project';
import { UserProfileInfo } from 'api/user';
import { ReactComponent as Crown } from 'assets/svgs/crown.svg';
import { Button } from 'components/atoms/button';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { BodyShort01, ProductiveHeading07 } from 'components/styles/typography';
import { updateMemberAction } from 'data/members/actions';
import {
  addMembersToProject,
  removeMembersFromProject,
  updateProjectMembers,
} from 'data/projects/actions';
import { useTranslation } from 'hooks/use-translation';
import { getProjectUsersLiteKey } from 'queries/common';
import { ALL_MEMBERS_QUERY_KEY, useMembersQuery } from 'queries/members';
import {
  getPlaceholderResolutionByProjectQueryKeyV2,
  PlaceholderResolutionsQueryKey,
} from 'queries/placeholder';
import { Membership, Memberships } from 'stores/memberships';
import { getUserLabel } from 'utils/get-user-label';
import { ManageRoleModal } from '../manage-role-modal';

const Text = styled.p`
  margin-bottom: 0;
`;

const MenuItem = styled(Menu.Item)`
  ${BodyShort01};
  padding: 0px;
`;

const CrownBlack = styled(Crown)`
  fill: var(--black-100);
`;

const ActionsHeading = styled(Text)`
  ${ProductiveHeading07};
  padding: var(--spacing-05) var(--spacing-05) var(--spacing-03);
`;

// READ - can leave
// WRITE - can add or update or create
// CREATE - add
// DELETE  - remove

const permissionsTypes = {
  [PermissionSetType.ProjectOwner]: ['READ', 'WRITE', 'CREATE', 'DELETE'], // can do all
  [PermissionSetType.Collaborator]: ['READ'],
  [PermissionSetType.CustomerCollaborator]: [
    'READ',
    'WRITE',
    'CREATE',
    'DELETE',
  ], // can edit only the same company
  [PermissionSetType.AccountAdmin]: ['READ', 'WRITE', 'CREATE', 'DELETE'],
  [PermissionSetType.SuperUser]: ['READ', 'WRITE', 'CREATE', 'DELETE'],
  [PermissionSetType.CustomerChampion]: ['READ', 'WRITE', 'CREATE', 'DELETE'],
  [PermissionSetType.PartnerCollaborator]: [
    'READ',
    'WRITE',
    'CREATE',
    'DELETE',
  ], // can edit only the same company
};

export const MoreActions: FC<{
  projectId: number;
  user: User;
  loggedInUserPermissions: Membership | undefined;
  membersMeta: MembersMeta | undefined;
  projectOwner: User;
  customerChampion?: User;
  loggedInUser: UserProfileInfo;
  memberships: Memberships;
}> = ({
  projectId,
  user,
  loggedInUserPermissions,
  membersMeta,
  projectOwner,
  loggedInUser,
  memberships,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const permissions = loggedInUserPermissions
    ? permissionsTypes[loggedInUserPermissions.memberType]
    : [];
  const membership = memberships.byUserId[user.userId];

  const errorTitle = useTranslation('failure');
  const resend = useTranslation('resend');
  const remove = useTranslation('remove');
  const removalConfirmTitle = useTranslation(
    'invitation.team_member.removal.confirm.title',
  );
  const removalSuccessTitle = useTranslation(
    'invitation.team_member.removal.success.title',
  );
  const removalSuccessContent = useTranslation(
    'invitation.team_member.removal.success.content',
  );
  const removalFailureContent = useTranslation(
    'invitation.team_member.removal.failure.content',
  );
  const resendConfirmTitle = useTranslation('invitation.resend.confirm.title');
  const resendConfirmSuccessTitle = useTranslation('invitation.success.title');
  const resendConfirmSuccessContent = useTranslation(
    'invitation.success.content',
  );
  const resendConfirmErrorContent = useTranslation(
    'invitation.resend.confirm.error.content',
  );
  const changeConfirm = {
    title: useTranslation('project.members.change_project_owner.title'),
    content: useTranslation('project.members.change_project_owner.content'),
    okText: useTranslation('change'),
  };
  const leaveConfirmation = {
    title: useTranslation('leave_project'),
    content: useTranslation('invitation.leave.confirm.content'),
    okText: useTranslation('leave'),
  };
  const leaveErrorContent = useTranslation('invitation.leave.error.content');
  const poChangeSuccessContent = useTranslation(
    'invitation.team_member.project_owner.success.content',
  );
  const successTitle = useTranslation('success');

  // TODO: see if can be even refactored @krishna
  const canWrite = permissions.includes('WRITE');
  const canRead = permissions.includes('READ');
  const canDelete = permissions.includes('DELETE');

  const canChangePO =
    canWrite &&
    user.userType === UserType.Native &&
    projectOwner.userId !== user.userId;

  const canResend =
    user.userType === UserType.External &&
    !membership?.joinedAt &&
    user.userId !== loggedInUser.user.userId;

  const canLeave = canRead && user.userId === loggedInUser.user.userId;

  const canRemove = canDelete && user.userId !== loggedInUser.user.userId;
  const canUpdateRole = user.userType === UserType.External;
  // ========= EVENT HANDLERS ========== //
  const queryClient = useQueryClient();

  const handleRemove = (removingUser: User, currentProjectId: number) => {
    dispatch(
      removeMembersFromProject({
        projectId: currentProjectId,
        members: [{ userId: removingUser.userId }],
        handleSuccess: () => {
          toastService({
            toastContainer: {},
            title: removalSuccessTitle,
            message: removalSuccessContent,
          }).success();
          queryClient.invalidateQueries(getProjectUsersLiteKey(projectId));
          queryClient.invalidateQueries(
            getPlaceholderResolutionByProjectQueryKeyV2(ProjectId(projectId)),
          );
          queryClient.invalidateQueries(PlaceholderResolutionsQueryKey);
        },
        handleFailure: () => {
          toastService({
            toastContainer: {},
            title: errorTitle,
            message: removalFailureContent,
          }).error();
        },
      }),
    );

    return true;
  };

  const handleLeave = (leavingUser: User, currentProjectId: number) => {
    confirm({
      ...leaveConfirmation,
      okType: 'danger',
      onOk() {
        dispatch(
          removeMembersFromProject({
            projectId: currentProjectId,
            members: [{ userId: leavingUser.userId }],
            handleSuccess() {
              return history.push('/');
            },
            handleFailure() {
              toastService({
                toastContainer: {},
                title: errorTitle,
                message: leaveErrorContent,
              }).error();
            },
          }),
        );
      },
    });
  };

  const handleMembersPermissionUpdate = (
    fromUser: User,
    toUser: User,
    currentProjectId: number,
    fromPermission: number,
    toPermission: number,
  ) => {
    dispatch(
      updateProjectMembers({
        projectId: currentProjectId,
        members: [
          {
            user: { userId: fromUser.userId },
            permissionSet: { permissionSetType: fromPermission },
          },
          {
            user: { userId: toUser.userId },
            permissionSet: { permissionSetType: toPermission },
          },
        ],
        handleSuccess: () => {
          toastService({
            toastContainer: {},
            title: successTitle,
            message: poChangeSuccessContent,
          }).success();
        },
      }),
    );
  };

  const confirmProjectOwnerChange = (
    updatingUser: User,
    _projectId: number,
  ) => {
    confirm({
      ...changeConfirm,
      onOk() {
        handleMembersPermissionUpdate(
          projectOwner,
          updatingUser,
          _projectId,
          PermissionSetType.Collaborator,
          PermissionSetType.ProjectOwner,
        );
      },
    });
  };

  const confirmMemberRemoval = (
    removingUser: User,
    currentProjectId: number,
  ) => {
    const taskAssignedCount = membersMeta?.projectMembersMeta?.find(
      (meta) => meta?.user.userId === removingUser.userId,
    )?.taskAssignedCount;

    const confirmConfig = {
      title: removalConfirmTitle,
      okText: remove,
      onOk() {
        handleRemove(removingUser, currentProjectId);
      },
    };

    if (taskAssignedCount && taskAssignedCount > 0) {
      confirm({
        ...confirmConfig,
        okType: 'danger',
        content: intl.formatMessage(
          { id: 'invitation.team_member.removal.confirm.with_tasks.content' },
          {
            count: taskAssignedCount,
            user: getUserLabel(removingUser),
            strong: (chunks) => <strong>{chunks}</strong>,
          },
        ),
      });
    } else {
      confirm({
        ...confirmConfig,
        okType: 'danger',
        content: intl.formatMessage(
          { id: 'invitation.team_member.removal.confirm.content' },
          { user: <b>{getUserLabel(removingUser)}</b> },
        ),
      });
    }
  };

  /**
   * While re-inviting the user, take the user permission thats already available and add the user to project
   */
  const confirmResendInvitation = (
    reinvitingUser: User,
    currentProjectId: number,
  ) => {
    const { userId } = reinvitingUser;
    const permissionSetType = membership.memberType;

    if (userId && permissionSetType) {
      confirm({
        title: resendConfirmTitle,
        content: intl.formatMessage(
          { id: 'invitation.resend.project.confirm.content' },
          {
            user: <b>{reinvitingUser.emailId}</b>,
          },
        ),
        okText: resend,
        onOk() {
          dispatch(
            addMembersToProject({
              projectId: currentProjectId,
              members: [
                {
                  user: {
                    userId,
                  },
                  permissionSet: { permissionSetType },
                },
              ],
              handleSuccess: () => {
                toastService({
                  toastContainer: {},
                  title: resendConfirmSuccessTitle,
                  message: resendConfirmSuccessContent,
                }).success();
              },
              handleFailure: () => {
                toastService({
                  toastContainer: {},
                  title: errorTitle,
                  message: resendConfirmErrorContent,
                }).error();
              },
            }),
          );
        },
      });
    }
  };
  const [roleUpdateModalVisible, setRoleUpdateModalVisibility] =
    useState(false);
  const showRoleUpdateModal = useCallback(() => {
    setRoleUpdateModalVisibility(true);
  }, []);
  const hideRoleUpdateModal = useCallback(() => {
    setRoleUpdateModalVisibility(false);
  }, []);
  const successMessage = useTranslation('settings.team_members.edit.success');
  const errorMessage = useTranslation('settings.team_members.edit.error');
  const { data: membersData } = useMembersQuery();

  const updateRoleForCustomer = useCallback(
    (roleId: number) => {
      return new Promise<void>((resolve, reject) => {
        const member = membersData?.members.find(
          ({ user: { userId } }) => user.userId === userId,
        );

        if (member == null) {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
          reject();

          return;
        }
        const formData = {
          user: {
            userId: user.userId,
            role: {
              roleId,
            },
            fields: user.fields,
            defaultCapacity: user.defaultCapacity,
            capacity: user.capacity,
          },
          permissionSet: {
            permissionSetType: member.permissionSet.permissionSetType,
          },
        };

        dispatch(
          updateMemberAction({
            data: [formData],
            role: {
              value: roleId.toString(),
              isNew: false,
            },
            async handleSuccess() {
              await queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
              await toastService({
                title: successTitle,
                message: successMessage,
              }).success();
              resolve();
            },
            handleFailure() {
              toastService({
                title: errorTitle,
                message: errorMessage,
              }).error();
              reject();
            },
          }),
        );
      });
    },
    [
      dispatch,
      errorMessage,
      errorTitle,
      membersData?.members,
      queryClient,
      successMessage,
      successTitle,
      user,
    ],
  );

  const menu = () => (
    <Menu>
      <ActionsHeading>
        <FormattedMessage id="more_actions" />
      </ActionsHeading>
      {/* Changing PO [Internal User] -
      - if has Write access
      - User type is Native
      - not already a PO
      - and Active */}

      {/*
- current user is EXTERNAL and this user is native => dont show the edits for NATIVE
- current user is NATIVE its normal
*/}
      {canChangePO && (
        <>
          <MenuItem>
            <Button
              type="text"
              icon={<CrownBlack />}
              block
              onClick={() => confirmProjectOwnerChange(user, Number(projectId))}
            >
              <FormattedMessage id="project.invite.make_project_owner" />
            </Button>
          </MenuItem>
        </>
      )}

      {/* Resend invitation
      - external user
      - is inactive
      - not current user */}

      {canResend && (
        <>
          <MenuItem>
            <Button
              type="text"
              icon={<Reset16 />}
              block
              onClick={() => confirmResendInvitation(user, Number(projectId))}
            >
              <FormattedMessage id="invitation.resend" />
            </Button>
          </MenuItem>
        </>
      )}
      {canUpdateRole ? (
        <MenuItem>
          <Button
            type="text"
            icon={<Identification16 />}
            block
            onClick={showRoleUpdateModal}
          >
            {user.role?.roleId == null ? (
              <FormattedMessage id="project.members.add_role.label" />
            ) : (
              <FormattedMessage id="project.members.update_role.label" />
            )}
          </Button>
        </MenuItem>
      ) : null}

      {/* Leave - Only if you are a member and not a PO */}
      {canLeave ? (
        projectOwner.userId === user.userId ? (
          <MenuItem>
            <Tooltip
              title={
                <FormattedMessage id="project.members.leave_project_owner.tooltip" />
              }
            >
              <Button
                type="text"
                icon={<Exit16 />}
                block
                danger
                onClick={() => handleLeave(user, Number(projectId))}
                disabled={projectOwner.userId === user.userId}
              >
                <FormattedMessage id="leave_project" />
              </Button>
            </Tooltip>
          </MenuItem>
        ) : (
          <Button
            type="text"
            icon={<Exit16 />}
            block
            danger
            onClick={() => handleLeave(user, Number(projectId))}
            disabled={projectOwner.userId === user.userId}
          >
            <FormattedMessage id="leave_project" />
          </Button>
        )
      ) : null}

      {/* Remove - if you have DELETE permissions and */}
      {canRemove ? (
        projectOwner.userId === user.userId ? (
          <MenuItem>
            <Tooltip
              title={
                <FormattedMessage id="project.members.leave_project_owner.tooltip" />
              }
            >
              <Button
                type="text"
                icon={<TrashCan16 />}
                block
                danger
                onClick={() => confirmMemberRemoval(user, Number(projectId))}
                disabled={projectOwner.userId === user.userId}
              >
                <FormattedMessage id="remove" />
              </Button>
            </Tooltip>
          </MenuItem>
        ) : (
          <Button
            type="text"
            icon={<TrashCan16 />}
            block
            danger
            onClick={() => confirmMemberRemoval(user, Number(projectId))}
            disabled={projectOwner.userId === user.userId}
          >
            <FormattedMessage id="remove" />
          </Button>
        )
      ) : null}
    </Menu>
  );

  return (
    <>
      {(canChangePO || canLeave || canRemove || canResend || canUpdateRole) && (
        <>
          <Dropdown overlay={menu} trigger={['click']}>
            <Button type="text" icon={<OverflowMenuVertical20 />} />
          </Dropdown>
          {roleUpdateModalVisible ? (
            <ManageRoleModal
              user={user}
              onSubmit={updateRoleForCustomer}
              onClose={hideRoleUpdateModal}
            />
          ) : null}
        </>
      )}
    </>
  );
};
